import { Injectable, computed, signal } from '@angular/core';
import packageJson from '../../../../package.json';

export interface AppVersionInfo {
  version: string;
  commit: string;
  buildDate: string;
  displayVersion: string;
}

const FALLBACK_VERSION: AppVersionInfo = {
  version: packageJson.version ?? '0.0.0',
  commit: 'unknown',
  buildDate: '',
  displayVersion: `${packageJson.version ?? '0.0.0'}+unknown`
};

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly versionInfoSignal = signal<AppVersionInfo>(FALLBACK_VERSION);
  private readonly initializedSignal = signal(false);

  readonly versionInfo = this.versionInfoSignal.asReadonly();
  readonly isLoaded = this.initializedSignal.asReadonly();
  readonly version = computed(() => this.versionInfoSignal().version);
  readonly commit = computed(() => this.versionInfoSignal().commit);
  readonly buildDate = computed(() => this.versionInfoSignal().buildDate);
  readonly displayVersion = computed(() => this.versionInfoSignal().displayVersion);

  async init(): Promise<void> {
    if (this.initializedSignal()) {
      return;
    }

    this.initializedSignal.set(true);

    try {
      this.versionInfoSignal.set(await this.loadVersionInfo());
    } catch {
      this.versionInfoSignal.set(FALLBACK_VERSION);
    }
  }

  async loadVersionInfo(options?: { bypassServiceWorker?: boolean }): Promise<AppVersionInfo> {
    const response = await fetch(this.buildVersionUrl(options?.bypassServiceWorker ?? false), {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`No se pudo cargar version.json (${response.status})`);
    }

    const payload = await response.json() as Partial<AppVersionInfo>;
    return this.normalizeVersionPayload(payload);
  }

  private normalizeVersionPayload(payload: Partial<AppVersionInfo>): AppVersionInfo {
    const version = payload.version?.trim() || FALLBACK_VERSION.version;
    const commit = payload.commit?.trim() || FALLBACK_VERSION.commit;
    const buildDate = payload.buildDate?.trim() || '';
    const displayVersion = payload.displayVersion?.trim() || `${version}+${commit}`;

    return {
      version,
      commit,
      buildDate,
      displayVersion
    };
  }

  private buildVersionUrl(bypassServiceWorker: boolean): string {
    if (!bypassServiceWorker) {
      return '/version.json';
    }

    return `/version.json?ngsw-bypass=true&t=${Date.now()}`;
  }
}
